import { Anchor, Badge, Group, Progress, Text } from "@mantine/core";
import { ExternalLink, Mail } from "lucide-react";

/**
 * Format a primitive field value to a display string based on its data type.
 */
export function formatFieldValueString(
  value: string | number | boolean | null | undefined,
  type: string,
): string {
  if (value === null || value === undefined || value === "") return "\u2014";

  const raw = String(value);

  switch (type) {
    case "currency": {
      if (raw.startsWith("$")) return raw;
      const num = parseFloat(raw.replace(/[^0-9.-]/g, ""));
      if (Number.isNaN(num)) return raw;
      if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
      if (Math.abs(num) >= 1_000) return `$${num.toLocaleString("en-US")}`;
      return `$${num.toFixed(2)}`;
    }
    case "number":
    case "int":
    case "float": {
      const num = parseFloat(raw.replace(/,/g, ""));
      if (Number.isNaN(num)) return raw;
      return num.toLocaleString("en-US");
    }
    case "percentage":
    case "percent": {
      const num = parseFloat(raw.replace(/[^0-9.-]/g, ""));
      if (Number.isNaN(num)) return raw;
      return `${num}%`;
    }
    case "datetime":
    case "date": {
      try {
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return raw;
        const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0;
        if (hasTime) {
          return d.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
          });
        }
        return d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      } catch {
        return raw;
      }
    }
    case "boolean": {
      const lower = raw.toLowerCase();
      if (lower === "true" || lower === "1" || lower === "yes") return "Yes";
      if (lower === "false" || lower === "0" || lower === "no") return "No";
      return raw;
    }
    case "duration_seconds": {
      const seconds = typeof value === "number" ? value : parseFloat(raw);
      if (Number.isNaN(seconds)) return raw;
      if (seconds < 60) return `${seconds}s`;
      if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
      if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
      return `${Math.round(seconds / 86400)}d`;
    }
    default:
      return raw;
  }
}

/**
 * Get CSS class name for a value based on type (numeric = monospace, etc.).
 */
export function getFieldValueClassName(
  value: string | number | boolean | null | undefined,
  type: string,
): string {
  if (value === null || value === undefined || value === "") return "text-gray-400";

  switch (type) {
    case "boolean": {
      const lower = String(value).toLowerCase();
      if (lower === "true" || lower === "1" || lower === "yes") return "text-green-600";
      if (lower === "false" || lower === "0" || lower === "no") return "text-gray-400";
      return "";
    }
    case "currency":
    case "number":
    case "int":
    case "float":
    case "percentage":
    case "percent":
      return "font-mono tabular-nums";
    default:
      return "";
  }
}

/**
 * Render a field value as a React node with rich formatting.
 * Handles URLs, emails, booleans, lists, health scores, etc.
 */
export function formatFieldValue(
  value: any,
  fieldDef?: { data_type?: string; display_format?: string; display_rules?: any; field_type?: string },
): React.ReactNode {
  if (value === null || value === undefined) return <span className="text-xs text-gray-400">{"\u2014"}</span>;

  const dataType = fieldDef?.data_type || "";
  const displayFormat = fieldDef?.display_format;

  // Health score rendering
  if (fieldDef?.field_type === "composite" && fieldDef?.display_rules?.type === "health_score") {
    const score = typeof value === "number" ? value : parseFloat(value);
    if (!Number.isNaN(score)) {
      const color = score >= 70 ? "green" : score >= 40 ? "yellow" : "red";
      return (
        <Group gap={8}>
          <Progress value={score} color={color} size="sm" className="w-20" />
          <Text size="xs" fw={500} c={color}>{score}</Text>
        </Group>
      );
    }
  }

  // Display format overrides
  if (displayFormat) {
    switch (displayFormat) {
      case "percentage":
        return <span className="text-xs font-mono tabular-nums">{typeof value === "number" ? `${value}%` : value}</span>;
      case "currency":
        return <span className="text-xs font-mono tabular-nums">{formatFieldValueString(value, "currency")}</span>;
      case "decimal_1":
        return <span className="text-xs">{typeof value === "number" ? value.toFixed(1) : value}</span>;
      case "decimal_2":
        return <span className="text-xs">{typeof value === "number" ? value.toFixed(2) : value}</span>;
      case "duration_seconds":
        return <span className="text-xs">{formatFieldValueString(value, "duration_seconds")}</span>;
      default:
        break;
    }
  }

  // List type
  if ((dataType === "list" || Array.isArray(value)) && Array.isArray(value)) {
    return (
      <div className="flex items-center gap-1 flex-wrap justify-end">
        {value.map((item: any, idx: number) => (
          <Badge key={idx} variant="light" color="gray" size="xs">
            {String(item)}
          </Badge>
        ))}
      </div>
    );
  }

  // Email type
  if (dataType === "email" || (typeof value === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))) {
    return (
      <Anchor href={`mailto:${value}`} size="xs" className="flex items-center gap-1">
        <Mail className="h-3 w-3" />
        {value}
      </Anchor>
    );
  }

  // URL type
  if (typeof value === "string" && (value.startsWith("http://") || value.startsWith("https://"))) {
    return (
      <Anchor href={value} target="_blank" size="xs" className="flex items-center gap-1">
        <span className="truncate max-w-[200px]">{value}</span>
        <ExternalLink className="h-3 w-3" />
      </Anchor>
    );
  }

  // Boolean
  if (typeof value === "boolean") {
    return <span className="text-xs">{value ? "Yes" : "No"}</span>;
  }

  // Object type
  if (typeof value === "object") {
    return <span className="text-xs text-gray-600">{JSON.stringify(value)}</span>;
  }

  // Use string formatter for standard types
  const resolvedType = displayFormat || dataType || "string";
  const formatted = formatFieldValueString(value, resolvedType);
  const className = getFieldValueClassName(value, resolvedType);

  return <span className={`text-xs text-gray-900 ${className}`}>{formatted}</span>;
}
